import { Color3, DirectionalLight, HemisphericLight, Vector3, type Scene } from "@babylonjs/core";
import type { SkySystem } from "./SkySystem";

// Palette tuned to the art-direction reference: warm, soft, low-contrast —
// dawn/dusk lean saturated orange, midday stays a gentle warm white rather
// than a stark cool white, night stays a muted deep blue rather than black.
const NIGHT_SUN_COLOR = new Color3(0.15, 0.2, 0.35);
const DAWN_DUSK_SUN_COLOR = new Color3(1.0, 0.62, 0.35);
const MIDDAY_SUN_COLOR = new Color3(1.0, 0.94, 0.82);

const NIGHT_FILL_COLOR = new Color3(0.08, 0.12, 0.22);
// Deliberately close to neutral, not a saturated sky-blue: HemisphericLight
// applies its diffuse color at *full* strength to any surface whose normal
// points straight up (sand, the top of the sky dome's usable range) — a
// strongly-blue fill color washes exactly those surfaces cyan regardless of
// their own albedo, which is what a first version of this const did to the
// sand. Keep this a gentle, near-white ambient lift; let the sun (and the
// sky gradient itself) carry the actual color mood.
const DAY_FILL_COLOR = new Color3(0.72, 0.71, 0.68);

// Sky gradient palette — soft pastel per the art direction, not physically
// simulated atmosphere. Zenith stays a gentle blue at all times of day;
// only the horizon band (and how saturated/warm it is) really shifts.
const NIGHT_SKY_TOP = new Color3(0.04, 0.06, 0.14);
const NIGHT_SKY_HORIZON = new Color3(0.1, 0.13, 0.24);
const DAWN_DUSK_SKY_TOP = new Color3(0.42, 0.4, 0.56);
const DAWN_DUSK_SKY_HORIZON = new Color3(0.96, 0.74, 0.58);
const DAY_SKY_TOP = new Color3(0.5, 0.7, 0.87);
const DAY_SKY_HORIZON = new Color3(0.86, 0.9, 0.93);

const MAX_SUN_ELEVATION_DEG = 72;
const DAWN_DUSK_BAND_DEG = 15; // elevation band over which dawn/dusk color dominates

/**
 * Drives sun/fill lighting and the sky's gradient colors from a 0..1
 * time-of-day value. As of Phase 3, driven externally by `DayClock` via
 * `setTimeOfDay()` each frame (see `SceneRoot.update()`) — `update()` (the
 * old Phase 1/2 self-advance) is kept only as a standalone fallback for
 * running/testing this class outside the full director stack.
 */
export class DayNightCycle {
  readonly sunLight: DirectionalLight;
  readonly fillLight: HemisphericLight;

  private readonly scene: Scene;
  private readonly skySystem: SkySystem;
  private cycleDurationMs: number;
  private timeOfDay: number; // 0..1, 0 = midnight

  constructor(scene: Scene, skySystem: SkySystem, cycleDurationMs = 120_000, startTimeOfDay = 0.5) {
    this.scene = scene;
    this.skySystem = skySystem;
    this.cycleDurationMs = cycleDurationMs;
    this.timeOfDay = startTimeOfDay;

    this.sunLight = new DirectionalLight("sunLight", Vector3.Down(), scene);
    this.sunLight.intensity = 3.0;

    this.fillLight = new HemisphericLight("fillLight", Vector3.Up(), scene);
    this.fillLight.intensity = 0.6;
    this.fillLight.groundColor = new Color3(0.35, 0.32, 0.28);

    this.applyTimeOfDay();
  }

  /** Advance the self-driven cycle. No-op once an external driver takes over via setTimeOfDay. */
  update(deltaMs: number): void {
    this.timeOfDay = (this.timeOfDay + deltaMs / this.cycleDurationMs) % 1;
    this.applyTimeOfDay();
  }

  setTimeOfDay(t: number): void {
    this.timeOfDay = ((t % 1) + 1) % 1;
    this.applyTimeOfDay();
  }

  getTimeOfDay(): number {
    return this.timeOfDay;
  }

  private applyTimeOfDay(): void {
    const elevationDeg = this.elevationForTimeOfDay(this.timeOfDay);
    const elevationRad = (elevationDeg * Math.PI) / 180;

    // Fixed azimuth arc (rises/sets along one consistent direction) with a
    // slight off-axis tilt so shadows read as directional rather than
    // perfectly axis-aligned.
    const azimuthRad = (30 * Math.PI) / 180;
    const towardSun = new Vector3(
      Math.cos(elevationRad) * Math.cos(azimuthRad),
      Math.sin(elevationRad),
      Math.cos(elevationRad) * Math.sin(azimuthRad)
    );

    this.sunLight.direction = towardSun.scale(-1);

    const dayAmount = clamp01(elevationDeg / MAX_SUN_ELEVATION_DEG); // 0 at/below horizon, 1 at peak
    const dawnDuskAmount = 1 - clamp01(Math.abs(elevationDeg) / DAWN_DUSK_BAND_DEG);

    // Blend: night -> dawn/dusk warm -> midday warm-white, driven by elevation.
    const aboveHorizon = elevationDeg > 0;
    const baseColor = aboveHorizon
      ? Color3.Lerp(DAWN_DUSK_SUN_COLOR, MIDDAY_SUN_COLOR, dayAmount)
      : NIGHT_SUN_COLOR;
    this.sunLight.diffuse = Color3.Lerp(baseColor, DAWN_DUSK_SUN_COLOR, aboveHorizon ? dawnDuskAmount * 0.4 : 0);
    // Peaked lower than an initial pass (4.0) — combined with the light
    // sand albedo and tonemap exposure, that blew all the way to near-white
    // and erased the tan color the art direction calls for.
    this.sunLight.intensity = aboveHorizon ? 1.2 + dayAmount * 1.3 : 0.05;

    this.fillLight.diffuse = Color3.Lerp(NIGHT_FILL_COLOR, DAY_FILL_COLOR, Math.max(dayAmount, dawnDuskAmount * 0.3));
    // The Phase 1 sand-washing bug (see plan status) was DAY_FILL_COLOR's
    // saturation, not its strength — that fix cut intensity too along with
    // color, more conservatively than needed, leaving mid-albedo subjects
    // (Johnny, in particular) reading muted/underlit. Restored partway now
    // that the color itself is safely neutral.
    this.fillLight.intensity = aboveHorizon ? 0.32 + dayAmount * 0.38 : 0.12;

    // Same night -> dawn/dusk -> day blend, applied to the sky gradient.
    const skyDayAmount = aboveHorizon ? dayAmount : 0;
    const skyDawnDuskAmount = aboveHorizon ? dawnDuskAmount : dawnDuskAmount * clamp01(1 + elevationDeg / DAWN_DUSK_BAND_DEG);
    const nightToDawnTop = Color3.Lerp(NIGHT_SKY_TOP, DAWN_DUSK_SKY_TOP, skyDawnDuskAmount);
    const nightToDawnHorizon = Color3.Lerp(NIGHT_SKY_HORIZON, DAWN_DUSK_SKY_HORIZON, skyDawnDuskAmount);
    const horizonColor = Color3.Lerp(nightToDawnHorizon, DAY_SKY_HORIZON, skyDayAmount);
    this.skySystem.setGradientColors(Color3.Lerp(nightToDawnTop, DAY_SKY_TOP, skyDayAmount), horizonColor);

    // Keep distant-fog color matched to the sky's own horizon color — a
    // fixed fog color reads as a jarring seam against a horizon that shifts
    // color across the day-night cycle (bloom exaggerates the mismatch into
    // a visible bright line right at the horizon).
    this.scene.fogColor = horizonColor;
  }

  private elevationForTimeOfDay(t: number): number {
    // Sunrise/sunset at t=0.25/0.75, peak at t=0.5, nadir at t=0.
    return Math.sin((t - 0.25) * Math.PI * 2) * MAX_SUN_ELEVATION_DEG;
  }
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
